/**
 * Offline Storage Manager (Day-15 Module 2)
 * 
 * Primary: IndexedDB
 * Fallback: localStorage
 * 
 * Collection: offline_queue
 */

const DB_NAME = 'getcare360_offline';
const DB_VERSION = 2;

// Store names
export const STORES = {
  OFFLINE_QUEUE: 'offline_queue',
  CACHED_DATA: 'cached_data',
  ATTACHMENTS: 'attachments',
  SYNC_STATE: 'sync_state',
};

let dbInstance = null;
let useLocalStorage = false;

/**
 * Check if IndexedDB is available
 */
function isIndexedDBAvailable() {
  try {
    if (!window.indexedDB) return false;
    // Test if IndexedDB actually works
    const test = window.indexedDB.open('test');
    test.onerror = () => false;
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Initialize IndexedDB or fallback to localStorage
 */
export async function initOfflineDB() {
  if (dbInstance) return dbInstance;

  // Check if IndexedDB is available
  if (!isIndexedDBAvailable()) {
    console.warn('IndexedDB not available, using localStorage fallback');
    useLocalStorage = true;
    return null;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.warn('IndexedDB error, falling back to localStorage');
      useLocalStorage = true;
      resolve(null);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // offline_queue store - main action queue
      if (!db.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        const queueStore = db.createObjectStore(STORES.OFFLINE_QUEUE, {
          keyPath: 'id',
          autoIncrement: false,
        });
        queueStore.createIndex('actionType', 'actionType');
        queueStore.createIndex('status', 'status');
        queueStore.createIndex('createdAt', 'createdAt');
      }

      // Cached data store
      if (!db.objectStoreNames.contains(STORES.CACHED_DATA)) {
        const cacheStore = db.createObjectStore(STORES.CACHED_DATA, {
          keyPath: 'key',
        });
        cacheStore.createIndex('expiry', 'expiry');
      }

      // Attachments store
      if (!db.objectStoreNames.contains(STORES.ATTACHMENTS)) {
        const attachStore = db.createObjectStore(STORES.ATTACHMENTS, {
          keyPath: 'id',
        });
        attachStore.createIndex('queueItemId', 'queueItemId');
        attachStore.createIndex('createdAt', 'createdAt');
      }

      // Sync state store
      if (!db.objectStoreNames.contains(STORES.SYNC_STATE)) {
        db.createObjectStore(STORES.SYNC_STATE, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Get database instance
 */
async function getDB() {
  if (useLocalStorage) return null;
  if (!dbInstance) {
    await initOfflineDB();
  }
  return dbInstance;
}

/**
 * Generate unique ID for offline actions
 */
export function generateOfflineId() {
  return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ─────────────────────────────────────────────────────────────────
// localStorage Fallback Helpers
// ─────────────────────────────────────────────────────────────────

function getLocalStorageKey(storeName) {
  return `gc360_${storeName}`;
}

function getFromLocalStorage(storeName) {
  try {
    const data = localStorage.getItem(getLocalStorageKey(storeName));
    return data ? JSON.parse(data) : [];
  } catch (e) {
    console.error('localStorage read error:', e);
    return [];
  }
}

function saveToLocalStorage(storeName, data) {
  try {
    localStorage.setItem(getLocalStorageKey(storeName), JSON.stringify(data));
  } catch (e) {
    console.error('localStorage write error:', e);
  }
}

// ─────────────────────────────────────────────────────────────────
// Generic Store Operations (IndexedDB with localStorage fallback)
// ─────────────────────────────────────────────────────────────────

/**
 * Add item to a store
 */
export async function addToStore(storeName, item) {
  if (useLocalStorage) {
    const items = getFromLocalStorage(storeName);
    items.push(item);
    saveToLocalStorage(storeName, items);
    return item.id;
  }

  const db = await getDB();
  if (!db) {
    // Fallback triggered during operation
    const items = getFromLocalStorage(storeName);
    items.push(item);
    saveToLocalStorage(storeName, items);
    return item.id;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.add(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Update item in a store
 */
export async function updateInStore(storeName, item) {
  if (useLocalStorage) {
    const items = getFromLocalStorage(storeName);
    const index = items.findIndex(i => i.id === item.id);
    if (index >= 0) {
      items[index] = item;
      saveToLocalStorage(storeName, items);
    }
    return item.id;
  }

  const db = await getDB();
  if (!db) {
    const items = getFromLocalStorage(storeName);
    const index = items.findIndex(i => i.id === item.id);
    if (index >= 0) {
      items[index] = item;
      saveToLocalStorage(storeName, items);
    }
    return item.id;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.put(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get item from store by key
 */
export async function getFromStore(storeName, key) {
  if (useLocalStorage) {
    const items = getFromLocalStorage(storeName);
    return items.find(i => i.id === key || i.key === key) || null;
  }

  const db = await getDB();
  if (!db) {
    const items = getFromLocalStorage(storeName);
    return items.find(i => i.id === key || i.key === key) || null;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all items from store
 */
export async function getAllFromStore(storeName) {
  if (useLocalStorage) {
    return getFromLocalStorage(storeName);
  }

  const db = await getDB();
  if (!db) {
    return getFromLocalStorage(storeName);
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete item from store
 */
export async function deleteFromStore(storeName, key) {
  if (useLocalStorage) {
    const items = getFromLocalStorage(storeName);
    const filtered = items.filter(i => i.id !== key && i.key !== key);
    saveToLocalStorage(storeName, filtered);
    return;
  }

  const db = await getDB();
  if (!db) {
    const items = getFromLocalStorage(storeName);
    const filtered = items.filter(i => i.id !== key && i.key !== key);
    saveToLocalStorage(storeName, filtered);
    return;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all items from store
 */
export async function clearStore(storeName) {
  if (useLocalStorage) {
    saveToLocalStorage(storeName, []);
    return;
  }

  const db = await getDB();
  if (!db) {
    saveToLocalStorage(storeName, []);
    return;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get items by index (IndexedDB only, falls back to filter for localStorage)
 */
export async function getByIndex(storeName, indexName, value) {
  if (useLocalStorage) {
    const items = getFromLocalStorage(storeName);
    return items.filter(i => i[indexName] === value);
  }

  const db = await getDB();
  if (!db) {
    const items = getFromLocalStorage(storeName);
    return items.filter(i => i[indexName] === value);
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Count items in store
 */
export async function countInStore(storeName) {
  if (useLocalStorage) {
    return getFromLocalStorage(storeName).length;
  }

  const db = await getDB();
  if (!db) {
    return getFromLocalStorage(storeName).length;
  }

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Check if using localStorage fallback
 */
export function isUsingLocalStorage() {
  return useLocalStorage;
}

/**
 * Day-8: Clear sensitive cached data on logout.
 *
 * Clears cached reads (participant/care-plan/shift briefs, etc.) and the
 * encrypted secure store. By default PRESERVES the offline_queue and pending
 * attachments so unsynced clinical actions are not lost. Pass force=true for a
 * full device wipe (hand-back scenario).
 */
export async function clearSensitiveOfflineData({ force = false } = {}) {
  try {
    await clearStore(STORES.CACHED_DATA);
  } catch (e) {
    console.warn('Failed to clear cached_data:', e);
  }
  try {
    const { secureStore } = await import('./secureStore');
    await secureStore.clear();
  } catch (e) {
    console.warn('Failed to clear secure store:', e);
  }
  if (force) {
    for (const s of [STORES.OFFLINE_QUEUE, STORES.ATTACHMENTS, STORES.SYNC_STATE]) {
      try { await clearStore(s); } catch (e) { console.warn('Failed to clear', s, e); }
    }
  }
}


// Initialize on module load
if (typeof window !== 'undefined') {
  initOfflineDB().catch(console.error);
}
